import React, { useState, useEffect } from 'react';
import { Typography, CircularProgress, Grid, Card, CardContent, Box, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { buildTimeSeriesParams, buildBreakdownParams } from '../utils/v2StatsHelpers';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ApiIcon from '@mui/icons-material/Api';
import TokenIcon from '@mui/icons-material/Token';

interface V2ModelEntity {
  entityId: string;
  entityName: string;
  totals: { requests: number; tokensIn: number; tokensOut: number };
}

interface AggregatedStats {
  totalApiCalls: number;
  totalTokensIn: number;
  totalTokensOut: number;
  totalLlmTokensIn: number;
  totalLlmTokensOut: number;
  totalPolicyViolations: number;
  totalAlerts: number;
  totalBlocks: number;
}

const AIStatistics: React.FC = () => {
  const { authState } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const [aggregated, setAggregated] = useState<AggregatedStats>({
    totalApiCalls: 0, totalTokensIn: 0, totalTokensOut: 0,
    totalLlmTokensIn: 0, totalLlmTokensOut: 0,
    totalPolicyViolations: 0, totalAlerts: 0, totalBlocks: 0,
  });
  const [modelEntities, setModelEntities] = useState<V2ModelEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const tsParams = buildTimeSeriesParams(null, null, '30d');
      const bdParams = buildBreakdownParams(null, null, '30d');

      const [timeSeriesRes, policyRes, modelsRes] = await Promise.all([
        withTokenRefresh(() => apiClient.getAiGatewayStats(tsParams as any)),
        withTokenRefresh(() => apiClient.getPolicyStats(tsParams as any)).catch(() => null),
        withTokenRefresh(() => apiClient.getAiGatewayBreakdown('models', bdParams as any)),
      ]);

      let totalApiCalls = 0, totalTokensIn = 0, totalTokensOut = 0;
      let totalPolicyTokensIn = 0, totalPolicyTokensOut = 0;

      if ((timeSeriesRes as any)?.success && (timeSeriesRes as any)?.series) {
        (timeSeriesRes as any).series.forEach((p: any) => {
          totalApiCalls += p.requests || 0;
          totalTokensIn += p.tokensIn || 0;
          totalTokensOut += p.tokensOut || 0;
          totalPolicyTokensIn += p.policyTokensIn || 0;
          totalPolicyTokensOut += p.policyTokensOut || 0;
        });
      }

      let totalViolations = 0, totalAlerts = 0, totalBlocks = 0;
      if ((policyRes as any)?.success && (policyRes as any)?.series) {
        (policyRes as any).series.forEach((p: any) => {
          totalViolations += p.violations || 0;
          totalAlerts += p.alerts || 0;
          totalBlocks += p.blocks || 0;
        });
      }

      setAggregated({
        totalApiCalls,
        totalTokensIn,
        totalTokensOut,
        totalLlmTokensIn: totalPolicyTokensIn,
        totalLlmTokensOut: totalPolicyTokensOut,
        totalPolicyViolations: totalViolations,
        totalAlerts,
        totalBlocks,
      });

      if ((modelsRes as any)?.success && (modelsRes as any)?.entities) {
        setModelEntities((modelsRes as any).entities.map((e: any) => ({
          entityId: e.entityId,
          entityName: e.entityName || e.entityId,
          totals: {
            requests: e.totals?.requests || 0,
            tokensIn: e.totals?.tokensIn || 0,
            tokensOut: e.totals?.tokensOut || 0,
          },
        })));
      }
    } catch (err) {
      console.error('[AIStatistics] Error loading stats:', err);
      setError('Failed to load AI statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  if (isLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <CircularProgress style={{ color: '#617BFF' }} />
        <p className="text-gray-600 text-lg font-medium mt-4">Loading AI statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto" style={{ margin: '32px auto' }}>
          <Typography
            style={{
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '30px',
              color: '#181D27',
              marginBottom: '8px',
            }}
          >
            AI Statistics
          </Typography>
          <div className="mt-8 text-center">
            <Typography color="error">{error}</Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          AI
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Configure and manage your AI integrations and language models
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6">

      {/* Statistics Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              Statistics
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              Monitor AI usage, token consumption, and policy compliance
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Reload */}
            <Tooltip title="Reload">
              <IconButton
                size="small"
                onClick={loadStats}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Stats Grid */}
      <div className="max-w-7xl mx-auto pb-8">
        <Grid container spacing={3}>
          {/* API Calls */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              style={{
                borderRadius: '12px',
                border: '1px solid #E9EAEB',
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <Typography
                    style={{
                      fontSize: '14px',
                      color: '#535862',
                      fontWeight: 500,
                    }}
                  >
                    API Calls
                  </Typography>
                  <ApiIcon style={{ color: '#617BFF', fontSize: 24 }} />
                </div>
                <Typography
                  style={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: '#181D27',
                  }}
                >
                  {formatNumber(aggregated.totalApiCalls)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Tokens In */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              style={{
                borderRadius: '12px',
                border: '1px solid #E9EAEB',
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <Typography
                    style={{
                      fontSize: '14px',
                      color: '#535862',
                      fontWeight: 500,
                    }}
                  >
                    Tokens In
                  </Typography>
                  <TrendingUpIcon style={{ color: '#10B981', fontSize: 24 }} />
                </div>
                <Typography
                  style={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: '#181D27',
                  }}
                >
                  {formatNumber(aggregated.totalTokensIn)}
                </Typography>
                <Typography
                  style={{
                    fontSize: '12px',
                    color: '#717680',
                    marginTop: '4px',
                  }}
                >
                  LLM: {formatNumber(aggregated.totalLlmTokensIn)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Tokens Out */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              style={{
                borderRadius: '12px',
                border: '1px solid #E9EAEB',
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <Typography
                    style={{
                      fontSize: '14px',
                      color: '#535862',
                      fontWeight: 500,
                    }}
                  >
                    Tokens Out
                  </Typography>
                  <TokenIcon style={{ color: '#3B82F6', fontSize: 24 }} />
                </div>
                <Typography
                  style={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: '#181D27',
                  }}
                >
                  {formatNumber(aggregated.totalTokensOut)}
                </Typography>
                <Typography
                  style={{
                    fontSize: '12px',
                    color: '#717680',
                    marginTop: '4px',
                  }}
                >
                  LLM: {formatNumber(aggregated.totalLlmTokensOut)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Policy Violations */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              style={{
                borderRadius: '12px',
                border: '1px solid #E9EAEB',
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <Typography
                    style={{
                      fontSize: '14px',
                      color: '#535862',
                      fontWeight: 500,
                    }}
                  >
                    Policy Violations
                  </Typography>
                  <WarningIcon style={{ color: '#F59E0B', fontSize: 24 }} />
                </div>
                <Typography
                  style={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: '#181D27',
                  }}
                >
                  {aggregated.totalPolicyViolations}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Alerts */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              style={{
                borderRadius: '12px',
                border: '1px solid #E9EAEB',
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <Typography
                    style={{
                      fontSize: '14px',
                      color: '#535862',
                      fontWeight: 500,
                    }}
                  >
                    Alerts
                  </Typography>
                  <NotificationsIcon style={{ color: '#EF4444', fontSize: 24 }} />
                </div>
                <Typography
                  style={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: '#181D27',
                  }}
                >
                  {aggregated.totalAlerts}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Blocks */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              style={{
                borderRadius: '12px',
                border: '1px solid #E9EAEB',
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <Typography
                    style={{
                      fontSize: '14px',
                      color: '#535862',
                      fontWeight: 500,
                    }}
                  >
                    Blocks
                  </Typography>
                  <BlockIcon style={{ color: '#DC2626', fontSize: 24 }} />
                </div>
                <Typography
                  style={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: '#181D27',
                  }}
                >
                  {aggregated.totalBlocks}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Tokens (Combined) */}
          <Grid item xs={12} sm={6} md={6}>
            <Card
              style={{
                borderRadius: '12px',
                border: '1px solid #E9EAEB',
                boxShadow: 'none',
              }}
            >
              <CardContent>
                <Typography
                  style={{
                    fontSize: '16px',
                    color: '#181D27',
                    fontWeight: 600,
                    marginBottom: '16px',
                  }}
                >
                  Total Token Usage
                </Typography>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Typography style={{ fontSize: '12px', color: '#717680' }}>
                      Total In
                    </Typography>
                    <Typography style={{ fontSize: '24px', fontWeight: 600, color: '#10B981' }}>
                      {formatNumber(aggregated.totalTokensIn)}
                    </Typography>
                  </div>
                  <div>
                    <Typography style={{ fontSize: '12px', color: '#717680' }}>
                      Total Out
                    </Typography>
                    <Typography style={{ fontSize: '24px', fontWeight: 600, color: '#3B82F6' }}>
                      {formatNumber(aggregated.totalTokensOut)}
                    </Typography>
                  </div>
                  <div>
                    <Typography style={{ fontSize: '12px', color: '#717680' }}>
                      LLM In
                    </Typography>
                    <Typography style={{ fontSize: '20px', fontWeight: 600, color: '#181D27' }}>
                      {formatNumber(aggregated.totalLlmTokensIn)}
                    </Typography>
                  </div>
                  <div>
                    <Typography style={{ fontSize: '12px', color: '#717680' }}>
                      LLM Out
                    </Typography>
                    <Typography style={{ fontSize: '20px', fontWeight: 600, color: '#181D27' }}>
                      {formatNumber(aggregated.totalLlmTokensOut)}
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* By Model */}
        {modelEntities.length > 1 && (
          <div className="mt-8">
            <Typography
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: '#181D27',
                marginBottom: '20px',
              }}
            >
              By Model
            </Typography>
            <Grid container spacing={3}>
              {modelEntities.map((entity) => (
                <Grid item xs={12} md={6} key={entity.entityId}>
                  <Card
                    style={{
                      borderRadius: '16px',
                      border: '1px solid #E9EAEB',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease',
                    }}
                    sx={{
                      '&:hover': {
                        boxShadow: '0 4px 12px 0 rgba(97, 123, 255, 0.1)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardContent style={{ padding: '24px' }}>
                      <Typography
                        style={{
                          fontSize: '15px',
                          fontWeight: 700,
                          color: '#181D27',
                          marginBottom: '16px',
                        }}
                      >
                        {entity.entityName}
                      </Typography>
                      <div className="grid grid-cols-3 gap-6">
                        <div>
                          <Typography style={{ fontSize: '12px', color: '#717680', marginBottom: '6px' }}>
                            Requests
                          </Typography>
                          <Typography style={{ fontSize: '22px', fontWeight: 700, color: '#617BFF' }}>
                            {formatNumber(entity.totals.requests)}
                          </Typography>
                        </div>
                        <div>
                          <Typography style={{ fontSize: '12px', color: '#717680', marginBottom: '6px' }}>
                            Tokens In
                          </Typography>
                          <Typography style={{ fontSize: '22px', fontWeight: 700, color: '#10B981' }}>
                            {formatNumber(entity.totals.tokensIn)}
                          </Typography>
                        </div>
                        <div>
                          <Typography style={{ fontSize: '12px', color: '#717680', marginBottom: '6px' }}>
                            Tokens Out
                          </Typography>
                          <Typography style={{ fontSize: '22px', fontWeight: 700, color: '#3B82F6' }}>
                            {formatNumber(entity.totals.tokensOut)}
                          </Typography>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default AIStatistics;
